import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { PlatformRole } from '@leanin/shared/views/profile';
import { takeAuthDrift, type MyContext } from '@/lib/queries/identity';
import { groupKeys } from '@/lib/queries/groups';

// Route context below the `_authed` gate: its `beforeLoad` resolved the
// viewer's session and merged it into the context every nested route sees.
type AuthedRouteContext = { readonly queryClient: QueryClient } & NonNullable<MyContext>;

/**
 * Returned `Response` from `redirect()`. Callers throw whatever the thunk
 * builds; passing a thunk (rather than a serialised object) lets each call
 * site keep TanStack's typed `to`/`params` narrowing.
 */
type BounceThunk = () => Response;

/**
 * The signed-out bounce: send the visitor to /sign-in, carrying the page they
 * asked for as `next` so signing in lands them back on it (omitted when the
 * page isn't a destination of its own). A drift-final verdict (restricted /
 * past-window deactivation) rides along as `reason` so the sign-in form
 * explains the forced sign-out.
 */
export function redirectToSignIn(next?: string): Response {
  const reason = takeAuthDrift();
  return redirect({
    to: '/sign-in',
    search: {
      ...(next !== undefined ? { next } : {}),
      ...(reason !== null ? { reason } : {}),
    },
  });
}

/** Bounce viewers whose `platformRole` doesn't match. */
export function requirePlatformRole(
  context: AuthedRouteContext,
  opts: { readonly role: PlatformRole; readonly bounceTo: BounceThunk },
): void {
  if (context.member.platformRole !== opts.role) throw opts.bounceTo();
}

/**
 * Group admin gate: passes platform admins through unconditionally, otherwise
 * fetches the viewer's access for `groupId` and bounces unless they may manage it.
 */
export async function requireLeaderOrAdmin(
  context: AuthedRouteContext,
  opts: { readonly groupId: string; readonly bounceTo: BounceThunk },
): Promise<void> {
  if (context.session.platformRole === 'admin') return;
  // Server-computed manage predicate — leader, parent-network leader, or
  // platform admin — the one the admin mutations enforce, so the gate can
  // never admit a viewer the server would refuse or bounce one it would admit.
  const { canManage } = await context.queryClient.ensureQueryData(
    groupKeys.viewerAccess(opts.groupId),
  );
  if (!canManage) throw opts.bounceTo();
}
